import * as React from "react";
import { PageContainer } from "../components/Card";
import { EmptyState, Banner } from "../components/States";
import { useUser, usePermission } from "../account/AccountStore";
import { Permissions } from "../account/permissions";
import { useAsync } from "../general/useAsync";
import { listCurriculumTemplates } from "../templates/CurriculumTemplatesStore";
import { CurriculumTemplateCard } from "../templates/CurriculumTemplateCard";
import { CurriculumTemplateWizard } from "../templates/CurriculumTemplateWizard";

/**
 * The landing page, at "/".
 *
 * Used to render one card per nav entry from `shell/navigation.ts` - a plain duplicate of the sidebar,
 * which already lists every screen. This is a curriculum-template picker instead: CAPS, Cambridge, IEB
 * and a generic Tertiary template, each of which can seed an organization's subjects, assessment
 * types, terms and a starter calculation procedure in one action. Nothing becomes less discoverable -
 * the sidebar still comes from the same unmodified `navigation.ts`.
 */

export const HomeIndex: React.FC = () => {
    const user = useUser();
    const canView = usePermission(Permissions.CONFIGURATION_READ);
    const canApply = usePermission(Permissions.CURRICULUM_APPLY);
    const templates = useAsync(() => listCurriculumTemplates(), [], { enabled: canView });
    const [openTemplateKey, setOpenTemplateKey] = React.useState<string | null>(null);

    const openTemplate = templates.data?.find((t) => t.key === openTemplateKey);

    return (
        <PageContainer>
            <div className="mb-8">
                <h1 className="text-2xl font-semibold text-text-strong">
                    {greeting()}
                    {user.name ? `, ${firstName(user.name)}` : ""}
                </h1>
                <p className="mt-1 text-sm text-text-weak">
                    {user.organization?.name ? (
                        <>
                            You are working in <strong className="text-text">{user.organization.name}</strong>. Everything below is scoped
                            to that organisation.
                        </>
                    ) : (
                        "No organisation is selected for this session."
                    )}
                </p>
            </div>

            {!canView && (
                <EmptyState
                    icon={["fad", "lock"]}
                    title="Nothing is available to you here"
                    hint="Your account has no assessment permissions for this organisation. If that looks wrong, try switching organisation from the menu at the top right."
                />
            )}

            {canView && (
                <section>
                    <div className="mb-3">
                        <h2 className="text-lg font-semibold tracking-tight text-text-strong">Curriculum templates</h2>
                        <p className="mt-1 text-sm text-text-weak">
                            Choose a starting point, then tailor the subjects, terms and assessment rules to your school.
                        </p>
                    </div>

                    {templates.error && <Banner tone="danger">{templates.error}</Banner>}

                    {templates.isLoading ? (
                        <p className="text-sm text-text-weak">Loading templates...</p>
                    ) : (
                        <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
                            {(templates.data ?? []).map((template) => (
                                <CurriculumTemplateCard key={template.key} template={template} onOpen={setOpenTemplateKey} />
                            ))}
                        </div>
                    )}
                </section>
            )}

            {openTemplate && (
                <CurriculumTemplateWizard
                    templateKey={openTemplate.key}
                    displayName={openTemplate.displayName}
                    description={openTemplate.description}
                    canApply={canApply}
                    onClose={() => setOpenTemplateKey(null)}
                    onChanged={templates.refresh}
                />
            )}
        </PageContainer>
    );
};

function greeting(): string {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 17) return "Good afternoon";
    return "Good evening";
}

function firstName(name: string): string {
    return name.trim().split(/\s+/)[0];
}

export default HomeIndex;
